import {
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
  OrderDirection,
  PageEvents,
} from '@griply/types';
import {
  filterGoals,
  filterGoalsByType,
  goalListFilters,
  goalOnlyTypes,
  hasActiveFilters,
} from '@griply/utils';
import { useGoalOverviewReorder, useSortedGoals } from 'features/goals';
import {
  useGoalsOverviewFilterDocument,
  useUpdateGoalsOverviewFilterDocument,
  useUpdateUserSettings,
} from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { GoalFilterList, SortBar } from 'shared/views/goal-filter-list';

import { useGoalsOverviewFilteredGoals } from '../../hooks/use-goals-overview-filtered-goals';
import { useGoalsOverviewOnSectionCollapse } from '../../hooks/use-goals-overview-on-section-collapse';
import { useGoalsOverviewOrder } from '../../hooks/use-goals-overview-order';
import { EmptyBanner } from '../empty-banner';
import { NoFilterResultsState } from '../no-filter-results-state';
import { TabPanel } from '../tab-panel';
import * as Styled from './group-by-list.style';

// The Goals list is goals-only; projects live on the Projects page.

export const GroupByList: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useTrackPageVisitEvent(PageEvents.GoalsList, undefined);
  const user = useUser();
  const sortedGoals = useSortedGoals();
  const links = useEntityLinks();

  const filterDocument = useGoalsOverviewFilterDocument();
  const updateFilterDocument = useUpdateGoalsOverviewFilterDocument();
  const updateUserSettings = useUpdateUserSettings();
  const updateCollapsedSections = useGoalsOverviewOnSectionCollapse();

  const showCompletedGoals =
    user?.settings?.goalsOverview?.roadmap?.showCompletedGoals ?? true;

  const filteredGoals = useMemo(() => {
    const typed = filterGoalsByType(sortedGoals, goalOnlyTypes);
    return filterGoals(typed, { links, hideCompleted: !showCompletedGoals });
  }, [links, sortedGoals, showCompletedGoals]);

  const groupBy =
    (filterDocument.groupByMode as GoalGroupBy) ?? GroupByMode.None;
  const sortingMode = filterDocument.sortingMode ?? undefined;
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;

  const onReorder = useGoalOverviewReorder(groupBy);
  const { ids: customOrder, goalChildrenByParentId } =
    useGoalsOverviewOrder(filterDocument);

  const onSortChange = useCallback(
    (mode: GoalSortingMode) => {
      updateFilterDocument({ sortingMode: mode });
      updateUserSettings({ goalSortDirection: OrderDirection.Asc });
    },
    [updateFilterDocument, updateUserSettings],
  );

  const onSortDirectionToggle = useCallback(() => {
    updateUserSettings({
      goalSortDirection:
        sortDirection === OrderDirection.Asc
          ? OrderDirection.Desc
          : OrderDirection.Asc,
    });
  }, [sortDirection, updateUserSettings]);

  // The fully filter-document-filtered list (life-area/start-date/deadline/
  // impact applied), used to detect "filters excluded every goal".
  const filterResultGoals = useGoalsOverviewFilteredGoals({
    filterDocument,
    showCompleted: showCompletedGoals,
    goalTypes: goalOnlyTypes,
  });
  const hasActiveFiltersValue = useMemo(
    () =>
      hasActiveFilters(filterDocument.filters, {
        supportedFilters: goalListFilters,
      }),
    [filterDocument.filters],
  );

  const onResetFilters = useCallback(
    () => updateFilterDocument({ filters: null }),
    [updateFilterDocument],
  );

  if (!filteredGoals.length) {
    return <EmptyBanner />;
  }

  if (!filterResultGoals.length && hasActiveFiltersValue) {
    return <NoFilterResultsState onReset={onResetFilters} />;
  }

  return (
    <TabPanel role="tab-panel" ref={scrollRef}>
      <Styled.GroupingContainer>
        <SortBar
          value={sortingMode}
          direction={sortDirection}
          onChange={onSortChange}
          onDirectionToggle={onSortDirectionToggle}
          hideLifeArea={groupBy === GroupByMode.LifeArea}
        />
        <GoalFilterList
          filterDocument={filterDocument}
          goalTypeFilter={goalOnlyTypes}
          onSectionCollapse={updateCollapsedSections}
          customOrder={customOrder}
          goalChildrenByParentId={goalChildrenByParentId}
          onReorder={onReorder}
          // The Archived section is always available at the bottom of the list,
          // regardless of the active grouping.
          hideArchived={false}
          scrollElement={scrollRef}
          hideCompleted={!showCompletedGoals}
        />
      </Styled.GroupingContainer>
    </TabPanel>
  );
};
